import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Phase 11c (#233) / #261: admin queue for refund requests. Originally
 * pickup-only; generalized to also surface NEGATIVE_BALANCE requests (server
 * auto-creates these when an order's balance goes below zero on a paid
 * order — see ezx-server #261).
 *
 * Canonical list and line-decision endpoints live under
 * /api/v2/admin/refund-requests.
 */

export type PickupRefundRequestKind = "DETACH" | "CANCEL" | "RESCHEDULE_REFUND";
// Ticket 07 (refund-approval-queue): PARTIALLY_APPROVED + SHIPMENT_CANCELLATION
// were server-real for months while this client rendered blank badges for them
// and no filter could reach them. The label/variant lookups are Record<union>,
// so a future missing member is a tsc error, not a blank badge — no runtime
// fallback, deliberately.
export type PickupRefundRequestStatus =
  | "PENDING"
  | "APPROVED"
  | "PARTIALLY_APPROVED"
  | "DENIED";
export type RefundRequestSource =
  | "PICKUP_DETACH"
  | "PICKUP_CANCEL"
  | "PICKUP_RESCHEDULE"
  | "NEGATIVE_BALANCE"
  | "SHIPMENT_CANCELLATION"
  | "ADMIN_ORDER_REFUND";

// Ticket 04/06: the server returns the component breakdown per ticket.
export type RefundLineStatus = "PENDING" | "APPROVED" | "DENIED";
export type RefundLineApproverType = "ADMIN" | "PROVIDER";
// Ticket 09: who decided a line. UNRECORDED = resolved before the decider
// existed; shown plainly, never attributed to anyone.
export type RefundLineDeciderKind =
  | "HUMAN_ADMIN"
  | "CARRIER"
  | "SYSTEM"
  | "UNRECORDED";
export type RefundChargeType =
  | "LABEL"
  | "PICKUP"
  | "DELIVERY"
  | "INSURANCE"
  | "PACKAGING"
  | "SIGNATURE"
  | "PROPERTY_TYPE_SURCHARGE"
  | "DISCOUNT"
  | "FEE";

export type RefundSettlementOutcome =
  | { settled: true; status: PickupRefundRequestStatus }
  | {
      settled: false;
      reason:
        | "not_ready"
        | "already_settled"
        | "refund_failed"
        | "partially_settled"
        | "manual_payout_required";
    };

export interface RefundRequestLineRow {
  id: string;
  shipmentId: string | null;
  chargeType: RefundChargeType;
  approverType: RefundLineApproverType;
  amountNetCents: number;
  taxCents: number;
  amountGrossCents: number;
  status: RefundLineStatus;
  approvedGrossCents: number | null;
  approvedNetCents: number | null;
  approvedTaxCents: number | null;
  denialReason: string | null;
  deciderKind: RefundLineDeciderKind | null;
  deciderRule: string | null;
  approvedAt: string | null;
  provider: { id: string; name: string } | null;
  approvedByCarrier: { id: string; email: string } | null;
  approvedByAdmin: { id: string; email: string } | null;
  refundPayment: {
    id: string;
    amountCents: number;
    status: string;
    method: string;
    parentPaymentId: string | null;
  } | null;
}

export interface RefundActionEventRow {
  id: string;
  type: string;
  audience: "ADMIN" | "CARRIER";
  actionNeeded: boolean;
  resolvedAt: string | null;
  reason: string | null;
  details: Record<string, unknown> | null;
  createdAt: string;
  refundLineId: string | null;
  actorInternal: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
  actorCarrier: { id: string; email: string; name: string | null } | null;
}

export interface ManualRefundPayoutRow {
  id: string;
  settlementGroupId: string;
  fundingPaymentId: string;
  method: "CASH" | "E_TRANSFER" | "WECHAT";
  currency: string;
  amountCents: number;
  returnTransportId: string | null;
  grossRefundCents: number | null;
  returnChargeOffsetCents: number | null;
  returnTotalGrossCents: number | null;
  returnFundingGroupCount: number | null;
  status: "AWAITING_PAYOUT" | "PAID" | "FAILED" | "RECONCILIATION_NEEDED";
  assignedAt: string;
  paidAt: string | null;
  confirmedAt: string | null;
  externalReference: string | null;
  note: string | null;
  evidenceUrl: string | null;
  failureReason: string | null;
  confirmedBy: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
  fundingPayment: {
    id: string;
    method: string;
    referenceNumber: string | null;
  };
}

export interface RefundSettlementGroupRow {
  id: string;
  fundingPaymentId: string;
  rail: "STRIPE" | "ACCOUNT_CREDIT" | "MANUAL";
  method: string;
  currency: string;
  status:
    | "READY"
    | "PROCESSING"
    | "AWAITING_MANUAL"
    | "FAILED"
    | "UNKNOWN"
    | "RAIL_CONFIRMED"
    | "SETTLED";
  grossRefundCents: number;
  returnChargeOffsetCents: number;
  externalRefundCents: number;
  attemptCount: number;
  refundPaymentId: string | null;
  chargePaymentId: string | null;
  providerRefundId: string | null;
  externalReference: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  processingStartedAt: string | null;
  settledAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RefundReturnTransportRow {
  id: string;
  status: "QUOTED" | "ACCEPTED" | "SETTLED" | "AMOUNT_DUE" | "FAILED";
  payer: "CUSTOMER" | "CARRIER" | "EZX" | "WAIVED";
  waivedCostBearer: "CARRIER" | "EZX" | null;
  costCarrierId: string | null;
  currency: string;
  effectiveNetCents: number;
  taxCents: number;
  totalChargeCents: number;
  grossRefundCents: number | null;
  netCashMovementCents: number | null;
  amountDueCents: number | null;
  refundPaymentId: string | null;
  chargePaymentId: string | null;
  amountDuePaymentId: string | null;
  acceptedAt: string | null;
  settledAt: string | null;
  settlementError: string | null;
  settlementGroups: RefundSettlementGroupRow[];
  carrierPayableEvent: {
    id: string;
    carrierId: string;
    amountCents: number;
    taxCents: number;
    status: string;
    appliedAt: string | null;
    createdAt: string;
  } | null;
}

export interface PickupRefundRequestRow {
  id: string;
  source: RefundRequestSource;
  pickupBookingId: string | null;
  shipmentId: string | null;
  orderId: string | null;
  priceAdjustmentId: string | null;
  kind: PickupRefundRequestKind | null;
  status: PickupRefundRequestStatus;
  requestedByCustomerId: string | null;
  requestedAt: string;
  reason: string | null;
  expectedAmountCents: number;
  handledBy: string | null;
  handledAt: string | null;
  adminResponse: string | null;
  approvedAmountCents: number | null;
  refundPaymentId: string | null;
  pickupBooking: {
    id: string;
    scheduledDate: string;
    status: string;
    pickupAddress: {
      line1: string;
      city: string;
      province: string;
      postalCode: string | null;
    };
    slot: { name: string; startTime: string; endTime: string };
  } | null;
  shipment: {
    id: string;
    shipmentNumber: number;
    trackingNumber: string | null;
    pickupPriceCents: number | null;
    sellAmountCents: number | null;
  } | null;
  lines: RefundRequestLineRow[];
  order: {
    id: string;
    orderNumber: number;
    totalCents: number;
    balanceCents: number;
    paymentStatus: string;
  } | null;
  priceAdjustment: {
    id: string;
    type: string;
    reason: string;
    amountCents: number;
    createdAt: string;
  } | null;
  requestedByCustomer: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
  refundPayment: {
    id: string;
    amountCents: number;
    status: string;
  } | null;
  requestedByInternalUser: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
  settledAt: string | null;
  settlementError: string | null;
  settlementGroups: RefundSettlementGroupRow[];
  manualPayouts: ManualRefundPayoutRow[];
  actionEvents: RefundActionEventRow[];
  returnTransports: RefundReturnTransportRow[];
}

export function useGetPickupRefundRequests(args?: {
  status?: PickupRefundRequestStatus;
  source?: RefundRequestSource;
  kind?: PickupRefundRequestKind;
  limit?: number;
  carrierId?: string;
  carrierIds?: string[];
  dispatchCenterId?: string;
}) {
  const { token } = useSession();
  return useQuery<PickupRefundRequestRow[]>({
    queryKey: ["refund-requests", args],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (args?.status) params.append("status", args.status);
      if (args?.source) params.append("source", args.source);
      if (args?.kind) params.append("kind", args.kind);
      if (args?.limit) params.append("limit", String(args.limit));
      if (args?.carrierId) params.append("carrierId", args.carrierId);
      for (const carrierId of args?.carrierIds ?? []) {
        params.append("carrierIds", carrierId);
      }
      if (args?.dispatchCenterId)
        params.append("dispatchCenterId", args.dispatchCenterId);
      const { data } = await axios.get<{
        data: PickupRefundRequestRow[];
      }>(
        `${API_URL}/api/v2/admin/refund-requests${
          params.toString() ? `?${params.toString()}` : ""
        }`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    enabled: !!token,
  });
}

export interface RefundableComponentOption {
  key: string;
  shipmentId: string | null;
  chargeType: RefundChargeType;
  providerCarrierId: string | null;
  providerCarrier: { id: string; name: string } | null;
  approverType: RefundLineApproverType;
  capturedNetCents: number;
  capturedTaxCents: number;
  capturedGrossCents: number;
  reservedNetCents: number;
  reservedTaxCents: number;
  reservedGrossCents: number;
  remainingNetCents: number;
  remainingTaxCents: number;
  remainingGrossCents: number;
  fundingPayments: Array<{
    id: string;
    method: string;
    status: string;
    referenceNumber: string | null;
    allocatedGrossCents: number;
  }>;
}

export type AdminRefundSelection =
  | { mode: "FULL" }
  | {
      mode: "COMPONENTS";
      components: Array<{
        shipmentId: string | null;
        chargeType: RefundChargeType;
      }>;
    }
  | {
      mode: "CUSTOM";
      amountGrossCents: number;
      allocations: Array<{
        shipmentId: string | null;
        chargeType: RefundChargeType;
        amountGrossCents: number;
      }>;
    }
  | { mode: "GOODWILL"; amountGrossCents: number };

export function useGetAdminRefundOptions(orderId: string, enabled = true) {
  const { token } = useSession();
  return useQuery({
    queryKey: ["refund-options", orderId],
    queryFn: async () => {
      const { data } = await axios.get<{
        data: { components: RefundableComponentOption[] };
      }>(`${API_URL}/api/v2/admin/orders/${orderId}/refund-options`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data.data.components;
    },
    enabled: enabled && Boolean(token) && Boolean(orderId),
  });
}

export function useCreateAdminRefundRequest(orderId: string) {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      reason: string;
      selection: AdminRefundSelection;
    }) => {
      const { data } = await axios.post<{ data: PickupRefundRequestRow }>(
        `${API_URL}/api/v2/admin/orders/${orderId}/refund-requests`,
        input,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["refund-options", orderId],
        }),
        queryClient.invalidateQueries({ queryKey: ["refund-requests"] }),
        queryClient.invalidateQueries({
          queryKey: ["order-payment-status", orderId],
        }),
      ]);
    },
  });
}

export function useConfirmManualRefundPayout() {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      payoutId: string;
      method: ManualRefundPayoutRow["method"];
      amountCents: number;
      externalReference: string;
      paidAt: string;
      note?: string;
      evidenceUrl?: string;
    }) => {
      const { payoutId, ...body } = input;
      const { data } = await axios.post<{ data: ManualRefundPayoutRow }>(
        `${API_URL}/api/v2/admin/manual-refund-payouts/${payoutId}/confirm`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["refund-requests"],
      });
    },
  });
}

export function useReconcileManualRefundPayout() {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      payoutId: string;
      action: "MARK_FAILED" | "FLAG_RECONCILIATION" | "REOPEN";
      reason: string;
    }) => {
      const { payoutId, ...body } = input;
      const { data } = await axios.post<{ data: ManualRefundPayoutRow }>(
        `${API_URL}/api/v2/admin/manual-refund-payouts/${payoutId}/reconcile`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["refund-requests"],
      });
    },
  });
}

export function useReconcileRefundSettlementIssue() {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      eventId: string;
      action: "RETRY_FAILED" | "RETRY_UNKNOWN_SAFELY";
      reason: string;
      evidenceReference?: string;
    }) => {
      const { eventId, ...body } = input;
      const { data } = await axios.post<{
        data: { refundRequestId: string; settlement: RefundSettlementOutcome };
      }>(
        `${API_URL}/api/v2/admin/refund-action-events/${eventId}/reconcile`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["refund-requests"] });
    },
  });
}

/**
 * Ticket 06: per-line resolution — the server has had these endpoints for
 * months; this client never called them.
 *   POST /api/v2/admin/refund-requests/:id/lines/:lineId/approve
 *     Body: { approvedGrossCents?, emergencyOverrideReason? }
 *     (admin reduction ≤ line gross; audited requester self-approval)
 *   POST /api/v2/admin/refund-requests/:id/lines/:lineId/deny
 *     Body: { denialReason }  (required)
 */
export function useApproveRefundLine() {
  const queryClient = useQueryClient();
  const { token } = useSession();
  return useMutation({
    mutationFn: async (args: {
      requestId: string;
      lineId: string;
      approvedGrossCents?: number;
      emergencyOverrideReason?: string;
    }) => {
      const body = {
        ...(args.approvedGrossCents !== undefined
          ? { approvedGrossCents: args.approvedGrossCents }
          : {}),
        ...(args.emergencyOverrideReason !== undefined
          ? { emergencyOverrideReason: args.emergencyOverrideReason }
          : {}),
      };
      const { data } = await axios.post<{
        data: {
          status: RefundLineStatus;
          settlement: RefundSettlementOutcome;
        };
      }>(
        `${API_URL}/api/v2/admin/refund-requests/${args.requestId}/lines/${args.lineId}/approve`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["refund-requests"],
      });
    },
  });
}

export function useDenyRefundLine() {
  const queryClient = useQueryClient();
  const { token } = useSession();
  return useMutation({
    mutationFn: async (args: {
      requestId: string;
      lineId: string;
      denialReason: string;
    }) => {
      const { data } = await axios.post<{
        data: {
          status: RefundLineStatus;
          settlement: RefundSettlementOutcome;
        };
      }>(
        `${API_URL}/api/v2/admin/refund-requests/${args.requestId}/lines/${args.lineId}/deny`,
        { denialReason: args.denialReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["refund-requests"],
      });
    },
  });
}
